import { memo } from 'react';
import type { CampaignHeaderProps } from './CampaignHeader.types';
import { getWidgetStores } from '@/entities/widget';
import { AsyncImage } from '../AsyncImage';
import { useTranslation } from 'react-i18next';

/**
 * CampaignHeader - Displays the campaign title, mission, and image collage
 *
 * Features:
 * - Responsive layout (horizontal in fullscreen, vertical in panel mode)
 * - Memoized to prevent unnecessary re-renders
 * - Accessible with proper semantic structure
 */
export const CampaignHeader = memo(function CampaignHeader({
  title,
  creativeMission,
  mainImage,
}: CampaignHeaderProps) {
  const { t } = useTranslation('contentTest');

  const { sidePanelLayoutMode } = getWidgetStores().uiStore.useSidePanelLayoutMode();
  const isFullscreen = sidePanelLayoutMode === 'fullscreen';

  // Don't render if no main image
  if (!mainImage) {
    return null;
  }

  return (
    <div
      className={`flex w-full items-center justify-between bg-accent px-15 ${
        isFullscreen ? 'flex-row py-15' : 'flex-col py-12'
      }`}
    >
      {/* Left Content */}
      <div className="flex flex-col gap-6 pb-8">
        <h1 className="text-display-md text-primary">{title}</h1>

        {/* Creative Mission */}
        <div className="flex flex-col gap-2">
          <h2 className="text-body-lg text-foreground">
            {t('ideationWidget.campaignHeader.creativeMission')}
          </h2>
          <p className="text-xl text-foreground">{creativeMission}</p>
        </div>
      </div>

      {/* Right Image Collage */}
      <div
        className={`relative shrink-0 overflow-hidden rounded-[20px] ${
          isFullscreen ? 'aspect-[594/335] h-83.75' : 'aspect-[651/367] h-91.75'
        }`}
        aria-label={t('ideationWidget.campaignHeader.campaignImagesCollage')}
        role="img"
      >
        {/* Main Center Image */}
        <div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-3xl ${
            isFullscreen ? 'aspect-[98/55] h-68.75' : 'aspect-[536/302] h-75.5'
          }`}
        >
          <AsyncImage
            assetId={mainImage.assetId}
            alt={t('ideationWidget.campaignHeader.mainCampaignImage')}
            variant="light"
            placeholderText=""
          />
        </div>
      </div>
    </div>
  );
});
